import { Quaternion } from 'three';
import type { PresenceViewport } from './protocol';

/** Interpolate only within the same view; crossing floors/modes is reconciled by the controller. */
export function interpolateViewport(from: PresenceViewport, to: PresenceViewport, t: number): PresenceViewport {
  if (t >= 1 || from.kind !== to.kind) return to;
  const mix = (a: number, b: number) => a + (b - a) * Math.max(0, t);
  if ((from.kind === 'floor' || from.kind === 'ceiling') && (to.kind === 'floor' || to.kind === 'ceiling')) return { ...to,
    centre: { x: mix(from.centre.x, to.centre.x), z: mix(from.centre.z, to.centre.z) },
    span: { width: mix(from.span.width, to.span.width), height: mix(from.span.height, to.span.height) } };
  if (from.kind === 'wall' && to.kind === 'wall') return from.wallId !== to.wallId || from.side !== to.side ? to : { ...to,
    zoom: mix(from.zoom, to.zoom), pan: { x: mix(from.pan.x, to.pan.x), y: mix(from.pan.y, to.pan.y) } };
  if ((from.kind === 'orbit' || from.kind === 'walk') && (to.kind === 'orbit' || to.kind === 'walk')) {
    if (from.wallMode !== to.wallMode || from.showCeiling !== to.showCeiling || from.isolatedRoom !== to.isolatedRoom) return to;
    const a = from.camera, b = to.camera;
    return { ...to, camera: { ...b,
      position: a.position.map((value, i) => mix(value, b.position[i])) as [number, number, number],
      target: a.target.map((value, i) => mix(value, b.target[i])) as [number, number, number],
      quaternion: new Quaternion().fromArray(a.quaternion).normalize().slerp(new Quaternion().fromArray(b.quaternion).normalize(), Math.max(0, t)).toArray(),
      fov: mix(a.fov, b.fov), walkFov: a.walkFov === null || b.walkFov === null ? b.walkFov : mix(a.walkFov, b.walkFov),
      pitch: mix(a.pitch, b.pitch), yaw: a.yaw + Math.atan2(Math.sin(b.yaw - a.yaw), Math.cos(b.yaw - a.yaw)) * Math.max(0, t),
    } };
  }
  return to;
}
